// =====================================================================
// tema.js — Modo claro e escuro, e o olho da senha.
//
// Duas coisas pequenas que os dois apps precisam, e que se fossem
// copiadas em cada um divergiriam na primeira semana.
//
// ─── TEMA ───────────────────────────────────────────────────────────
//
// A primeira visita segue o sistema operacional (quem já usa o Mac no
// escuro não quer levar um clarão às onze da noite). A partir do momento
// em que a pessoa escolhe, a escolha dela vence — e sobrevive ao refresh.
//
// O tema é um atributo no <html>, e todo o resto é CSS. Nenhuma tela
// precisa saber que existe modo escuro: se precisasse, a próxima tela
// que alguém escrever vai esquecer.
//
// ─── OLHO DA SENHA ──────────────────────────────────────────────────
//
// ativarOlhos(raiz) percorre os campos de senha e põe o botão. É chamado
// depois de desenhar a tela, e não escrito à mão em cada formulário —
// senão o próximo formulário de senha nasce sem ele.
// =====================================================================

import { icon } from './icons.js';

const CHAVE = 'sgi:tema';

/** Chame uma vez, o mais cedo possível — antes de pintar a tela. */
export function iniciarTema() {
  const salvo = localStorage.getItem(CHAVE);
  const escuro = salvo
    ? salvo === 'escuro'
    : window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

  document.documentElement.dataset.tema = escuro ? 'escuro' : 'claro';

  // Se a pessoa nunca escolheu, continuamos seguindo o sistema — inclusive
  // quando ele muda sozinho ao anoitecer.
  if (!salvo && window.matchMedia) {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (!localStorage.getItem(CHAVE)) {
        document.documentElement.dataset.tema = e.matches ? 'escuro' : 'claro';
        sincronizarBotoes();
      }
    });
  }
}

export function temaAtual() {
  return document.documentElement.dataset.tema === 'escuro' ? 'escuro' : 'claro';
}

export function alternarTema() {
  const novo = temaAtual() === 'escuro' ? 'claro' : 'escuro';
  document.documentElement.dataset.tema = novo;
  localStorage.setItem(CHAVE, novo);   // a partir daqui, a escolha é dela
  sincronizarBotoes();
  return novo;
}

/**
 * Botão de tema: só o ícone. Sol quando está escuro (é o que você vai
 * ligar), lua quando está claro. O ícone mostra o DESTINO, não o estado —
 * é a convenção que as pessoas já têm no corpo, de tanto usar celular.
 */
export function botaoTemaHtml(classe = 'icone') {
  return `<button class="${classe} btn-tema" type="button"
            title="Alternar claro e escuro" aria-label="Alternar tema"></button>`;
}

/** Liga todos os botões de tema presentes na tela. Idempotente. */
export function ligarTema(raiz = document) {
  raiz.querySelectorAll('.btn-tema').forEach(b => { b.onclick = alternarTema; });
  sincronizarBotoes();
}

function sincronizarBotoes() {
  const escuro = temaAtual() === 'escuro';
  document.querySelectorAll('.btn-tema').forEach(b => {
    b.innerHTML = icon(escuro ? 'sol' : 'lua');
    b.title = escuro ? 'Voltar ao modo claro' : 'Ativar o modo escuro';
  });
}

// ─── Olho da senha ───────────────────────────────────────────────────
/**
 * Põe o botão de ver/ocultar em todo campo de senha dentro de 'raiz'.
 *
 * O campo volta a 'password' quando o formulário é enviado: senha visível
 * numa tela esquecida aberta é senha vazada. Mostrar é para conferir o que
 * se digitou, não para deixar exposto.
 */
export function ativarOlhos(raiz = document) {
  raiz.querySelectorAll('input[type="password"]').forEach((inp) => {
    if (inp.dataset.olho) return;      // já tem
    inp.dataset.olho = '1';

    const capa = document.createElement('div');
    capa.className = 'campo-senha';
    inp.parentNode.insertBefore(capa, inp);
    capa.appendChild(inp);

    const btn = document.createElement('button');
    btn.type = 'button';               // dentro de <form>, sem type ele SUBMETE
    btn.className = 'olho';
    btn.setAttribute('aria-label', 'Mostrar senha');
    btn.innerHTML = icon('ver');
    capa.appendChild(btn);

    btn.onclick = () => {
      const vendo = inp.type === 'text';
      inp.type = vendo ? 'password' : 'text';
      btn.innerHTML = icon(vendo ? 'ver' : 'ocultar');
      btn.setAttribute('aria-label', vendo ? 'Mostrar senha' : 'Ocultar senha');
      inp.focus();
    };

    const form = inp.closest('form');
    if (form) form.addEventListener('submit', () => {
      inp.type = 'password';
      btn.innerHTML = icon('ver');
    });
  });
}
